import { useCallback, useEffect, useRef, useState } from 'react';
import { activeAudioClips, activeVideoLayers, clipGainDb, dbToGain, projectDuration, toFrame, trackLevel, type QuickEditorProject } from '../../../utils/quickEditor';

interface PlaybackOptions {
  project: QuickEditorProject;
  /** A playable URL per asset id (object URLs of the local copies). */
  urls: Record<string, string>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  /** The timeline's frame rate: the playhead rests on frame starts. */
  frameRate: number;
  /** Called every frame with the current time, so the playhead can move without a React render. */
  onFrame?: (timeSec: number, playing: boolean) => void;
}

export interface EditorPlayback {
  /** Current time for readouts; updated on seeks and a few times a second while playing. */
  timeSec: number;
  isPlaying: boolean;
  /** The exact current time (not a render snapshot). */
  getTime: () => number;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seek: (timeSec: number) => void;
}

/** Tolerances before a media element is re-seeked to the timeline clock. */
const PLAY_DRIFT_SEC = 0.3;
const SCRUB_DRIFT_SEC = 0.03;
/** How long the preview keeps its last picture while a layer finds its frame, before showing it missing. */
const HOLD_PICTURE_MS = 2000;

/**
 * A video that is seeking, or still loading, has no picture for a moment (its readyState
 * drops below HAVE_CURRENT_DATA, and drawing it draws nothing). One that failed never will.
 */
export const isFindingFrame = (el: Pick<HTMLVideoElement, 'readyState' | 'error'>): boolean =>
  el.readyState < 2 && !el.error;

/**
 * The preview engine. One clock drives the timeline; each asset gets a hidden
 * video element (muted; a second one when the same asset is seen on two layers
 * at once) and each audio clip an audio element. Every frame the elements that
 * should be active are kept at the right time, the visible video layers are
 * painted bottom first at their tracks' opacity, and every audible clip plays at
 * its track's volume, so a cut between two clips is seamless and picture and
 * sound stay together.
 */
export const useEditorPlayback = ({ project, urls, canvasRef, frameRate, onFrame }: PlaybackOptions): EditorPlayback => {
  const frameRateRef = useRef(frameRate);
  frameRateRef.current = frameRate;
  const projectRef = useRef(project);
  projectRef.current = project;
  const urlsRef = useRef(urls);
  urlsRef.current = urls;
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;

  const timeRef = useRef(0);
  const playingRef = useRef(false);
  const clockRef = useRef({ wall: 0, time: 0 });
  const dirtyRef = useRef(true);
  const holdSinceRef = useRef<number | null>(null);
  const lastUiRef = useRef(0);
  const videoEls = useRef(new Map<string, { el: HTMLVideoElement; url: string }>());
  const audioEls = useRef(new Map<string, { el: HTMLAudioElement; url: string }>());

  const [timeSec, setTimeSec] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const markDirty = useCallback(() => { dirtyRef.current = true; }, []);

  /** The video element under `key`: the asset's id, with a slot number for each layer showing it at once. */
  const ensureVideo = useCallback((key: string, assetId: string): HTMLVideoElement | null => {
    const url = urlsRef.current[assetId];
    if (!url) return null;
    const hit = videoEls.current.get(key);
    if (hit && hit.url === url) return hit.el;
    hit?.el.pause();
    const el = document.createElement('video');
    el.muted = true;
    el.playsInline = true;
    el.preload = 'auto';
    el.addEventListener('seeked', markDirty);
    el.addEventListener('loadeddata', markDirty);
    el.src = url;
    videoEls.current.set(key, { el, url });
    return el;
  }, [markDirty]);

  const ensureAudio = useCallback((clipId: string, assetId: string): HTMLAudioElement | null => {
    const url = urlsRef.current[assetId];
    if (!url) return null;
    const hit = audioEls.current.get(clipId);
    if (hit && hit.url === url) return hit.el;
    hit?.el.pause();
    const el = new Audio();
    el.preload = 'auto';
    el.src = url;
    audioEls.current.set(clipId, { el, url });
    return el;
  }, []);

  /** Paint the layers bottom first, each fitted to the frame at its opacity. */
  const paint = useCallback((layers: { el: HTMLVideoElement; opacity: number }[]) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(rect.width * dpr));
    const height = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, width, height);
    for (const { el, opacity } of layers) {
      if (opacity <= 0 || el.readyState < 2 || !el.videoWidth || !el.videoHeight) continue;
      const scale = Math.min(width / el.videoWidth, height / el.videoHeight);
      const drawWidth = el.videoWidth * scale;
      const drawHeight = el.videoHeight * scale;
      ctx.globalAlpha = opacity;
      ctx.drawImage(el, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
    }
    ctx.globalAlpha = 1;
  }, [canvasRef]);

  useEffect(() => {
    let frame = 0;
    const tick = () => {
      frame = requestAnimationFrame(tick);
      const current = projectRef.current;
      const total = projectDuration(current);

      if (playingRef.current) {
        let next = clockRef.current.time + (performance.now() - clockRef.current.wall) / 1000;
        if (next >= total) {
          next = total;
          playingRef.current = false;
          setIsPlaying(false);
          setTimeSec(next);
          dirtyRef.current = true;
        }
        timeRef.current = next;
      }
      const time = timeRef.current;
      const playing = playingRef.current;

      // Picture: every visible layer under the playhead, bottom first. The same asset on two
      // layers at once gets a second element, so each can be at its own time.
      const seen = new Map<string, number>();
      const layers = activeVideoLayers(current, time).map((layer) => {
        const slot = seen.get(layer.clip.assetId) ?? 0;
        seen.set(layer.clip.assetId, slot + 1);
        return { ...layer, key: slot === 0 ? layer.clip.assetId : `${layer.clip.assetId}#${slot}` };
      });
      const shownKeys = new Set(layers.map(layer => layer.key));
      for (const [key, entry] of videoEls.current) {
        if (!shownKeys.has(key) && !entry.el.paused) entry.el.pause();
      }
      const shown: { el: HTMLVideoElement; opacity: number }[] = [];
      // Scrub seeks wait until after painting, so a frame that just arrived is shown before the next seek replaces it
      const scrubSeeks: { el: HTMLVideoElement; target: number }[] = [];
      for (const { clip: videoClip, opacity, key } of layers) {
        const el = ensureVideo(key, videoClip.assetId);
        if (!el) continue;
        const target = videoClip.inSec + (time - videoClip.startSec);
        if (playing) {
          if (el.paused) {
            if (Math.abs(el.currentTime - target) > 0.05) el.currentTime = target;
            void el.play().catch(() => undefined);
          } else if (Math.abs(el.currentTime - target) > PLAY_DRIFT_SEC) {
            el.currentTime = target;
          }
        } else {
          if (!el.paused) el.pause();
          // The middle of the frame: a frame start kept to the millisecond can sit just before the
          // frame it starts, and the video would show the one before
          scrubSeeks.push({ el, target: Math.min(target + 0.5 / frameRateRef.current, videoClip.outSec - 0.001) });
        }
        shown.push({ el, opacity });
      }

      // Sound: every clip on an unmuted audio track under the playhead, only while playing, at its track's volume and its own gain.
      const audible = playing ? activeAudioClips(current, time) : [];
      const audibleIds = new Set(audible.map(clip => clip.id));
      for (const [clipId, entry] of audioEls.current) {
        if (!audibleIds.has(clipId) && !entry.el.paused) entry.el.pause();
      }
      for (const clip of audible) {
        const el = ensureAudio(clip.id, clip.assetId);
        if (!el) continue;
        const track = current.tracks.find(t => t.id === clip.trackId);
        // The track's volume, then the clip's own gain
        const volume = (track ? trackLevel(track) : 1) * dbToGain(clipGainDb(clip));
        if (Math.abs(el.volume - volume) > 0.001) el.volume = volume;
        const target = clip.inSec + (time - clip.startSec);
        if (el.paused) {
          if (Math.abs(el.currentTime - target) > 0.05) el.currentTime = target;
          void el.play().catch(() => undefined);
        } else if (Math.abs(el.currentTime - target) > PLAY_DRIFT_SEC) {
          el.currentTime = target;
        }
      }

      if (playing || dirtyRef.current) {
        // While a layer finds its frame (every scrub step seeks), keep the last picture
        // instead of painting black where it goes; it is painted as soon as the frame is there
        const finding = shown.some(({ el, opacity }) => opacity > 0 && isFindingFrame(el));
        const now = performance.now();
        if (finding && (holdSinceRef.current === null || now - holdSinceRef.current < HOLD_PICTURE_MS)) {
          holdSinceRef.current ??= now;
        } else {
          holdSinceRef.current = null;
          paint(shown);
          dirtyRef.current = false;
        }
      }
      for (const { el, target } of scrubSeeks) {
        if (!el.seeking && Math.abs(el.currentTime - target) > SCRUB_DRIFT_SEC) el.currentTime = target;
      }
      onFrameRef.current?.(time, playing);
      if (playing) {
        const now = performance.now();
        if (now - lastUiRef.current > 100) {
          lastUiRef.current = now;
          setTimeSec(time);
        }
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [ensureAudio, ensureVideo, paint]);

  // Repaint when the edit, the media or the preview size changes.
  useEffect(() => { dirtyRef.current = true; }, [project, urls]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(markDirty);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [canvasRef, markDirty]);

  // Audio elements belong to clips; drop those whose clip is gone.
  useEffect(() => {
    const ids = new Set(project.clips.map(clip => clip.id));
    for (const [clipId, entry] of audioEls.current) {
      if (!ids.has(clipId)) {
        entry.el.pause();
        audioEls.current.delete(clipId);
      }
    }
  }, [project.clips]);

  // Release every media element with the node.
  useEffect(() => {
    const videos = videoEls.current;
    const audios = audioEls.current;
    return () => {
      for (const { el } of videos.values()) { el.pause(); el.removeAttribute('src'); el.load(); }
      for (const { el } of audios.values()) { el.pause(); el.removeAttribute('src'); el.load(); }
      videos.clear();
      audios.clear();
    };
  }, []);

  const seek = useCallback((seconds: number) => {
    const total = projectDuration(projectRef.current);
    // On the start of the nearest frame, so a cut or a mark at the playhead falls between frames
    const time = Math.min(Math.max(0, toFrame(seconds, frameRateRef.current)), total);
    timeRef.current = time;
    clockRef.current = { wall: performance.now(), time };
    dirtyRef.current = true;
    setTimeSec(time);
  }, []);

  const play = useCallback(() => {
    const total = projectDuration(projectRef.current);
    if (total <= 0) return;
    if (timeRef.current >= total - 0.01) timeRef.current = 0;
    clockRef.current = { wall: performance.now(), time: timeRef.current };
    playingRef.current = true;
    setIsPlaying(true);
  }, []);

  const pause = useCallback(() => {
    playingRef.current = false;
    // Stopped on a frame start, like a seek
    timeRef.current = Math.min(Math.max(0, toFrame(timeRef.current, frameRateRef.current)), projectDuration(projectRef.current));
    clockRef.current = { wall: performance.now(), time: timeRef.current };
    dirtyRef.current = true;
    setIsPlaying(false);
    setTimeSec(timeRef.current);
  }, []);

  const toggle = useCallback(() => {
    if (playingRef.current) pause();
    else play();
  }, [pause, play]);

  const getTime = useCallback(() => timeRef.current, []);

  return { timeSec, isPlaying, getTime, play, pause, toggle, seek };
};
